import type { FieldType } from '@heidi/runtime/analytics-vocabulary';
import type { LanguageCode, OrganisationRole } from '@heidi/schema/ml-scribe';
import type { CountryType } from '@heidi/utils/countries';
import type { ComponentType, JSXElementConstructor } from 'react';
import type { MessageDescriptor } from 'react-intl';
import type { Country } from 'react-phone-number-input';

import type { OnboardingEmrOption } from './onboarding-host';
import type { ClinicianTypeInput } from './personalizationTypes';

export type OnboardingPhoneNumberVariant = 'hidden' | 'optional' | 'required';

export type OnboardingIntegrationProvider = { uniqueId: string; name: string };

export type OnboardingIntegrationProvidersQuery =
  | { isSuccess: true; isError: boolean; data: OnboardingIntegrationProvider[] }
  | {
      isSuccess: false;
      isError: boolean;
      data: OnboardingIntegrationProvider[] | undefined;
    };

export type OnboardingFieldTracking = {
  trackImmediate: (fieldName: string, fieldType: FieldType, fieldValue?: string) => void;
  trackTextDebounced: (fieldName: string) => void;
};

export type OnboardingAboutYouAnalyticsParams = {
  country: CountryType | null;
  hasCountryPhoneMismatch: boolean;
  isPhoneFieldVisible: boolean;
  isPhoneNumberOptional: boolean;
  onSpecialtyChange: (value: ClinicianTypeInput | null) => void;
  onRoleChange: (value: OrganisationRole | null) => void;
  onCountryChange: (value: CountryType | null) => void;
  onJurisdictionChange: (value: string) => void;
  onPhoneNumberChange: (value: string) => void;
};

export type OnboardingAboutYouAnalytics = {
  handleSpecialtyChange: (value: ClinicianTypeInput | null | undefined) => void;
  handleRoleChange: (value: OrganisationRole) => void;
  handleCountryChange: (value: CountryType | null) => void;
  handleJurisdictionChange: (value: string) => void;
  handlePhoneNumberChange: (value: string) => void;
};

export type OnboardingCqcPracticeAutocompleteProps = {
  value: string;
  onValueChange: (value: string) => void;
  onPracticeSelect?: (isOfficial: boolean, practiceId?: string) => void;
  placeholder?: string;
};

export type OnboardingClinicianTypeSelectProps = {
  id?: string;
  selectedSpecialty?: ClinicianTypeInput;
  setSelectedSpecialty: (specialty: ClinicianTypeInput) => void;
  otherSpecialty?: string;
  setOtherSpecialty: (value?: string) => void;
  className?: string;
  countryCode?: string;
};

export type OnboardingCountrySelectProps = {
  value?: CountryType;
  setValue: (value: CountryType) => void;
  regionFilter?: string;
  extraCountries?: CountryType[];
  className?: string;
  popoverClassName?: string;
};

export type OnboardingJurisdictionSelectProps = {
  countryName?: string | null;
  value?: string | null;
  onChange: (jurisdictionCode: string) => void;
  className?: string;
};

export type OnboardingPhoneInputProps = {
  value: string;
  onChange: (value?: string) => void;
  allowedCountries: Country[];
  defaultCountry?: Country;
  placeholder?: string;
  className?: string;
};

export type OnboardingProFlashIconProps = {
  alt?: string;
  className?: string;
};

export type OnboardingSpecialty = ClinicianTypeInput & { localizedName: string };

export type OnboardingSpecialtiesResponse = {
  specialties: OnboardingSpecialty[];
  total: number;
};

export type OnboardingLocaleSyncOptions = {
  keepQuery?: boolean;
};

export interface OnboardingProfileHost {
  getPhoneNumberVariantForCountry: (
    countryCode: string | undefined
  ) => OnboardingPhoneNumberVariant;
  isUkPracticeSearchEnabled: () => boolean | undefined;
  isUkPracticeSearchMandatoryEnabled: () => boolean | undefined;
  useIsCountrySpecificEhrSelectionEnabled: () => boolean;
  useIsWorkspaceOnboardingRenameEnabled: () => boolean;
  useQueryIntegrationProviders: (args: {
    country: string | undefined;
    enabled?: boolean;
  }) => OnboardingIntegrationProvidersQuery;
  integrationProviderOtherId: string;
  useSelectedEmrOption: (
    emrEhr: string | null | undefined,
    countryCode: string | undefined
  ) => OnboardingEmrOption | undefined;
  useOnboardingFieldTracking: (section: string) => OnboardingFieldTracking;
  CQCPracticeAutocomplete: ComponentType<OnboardingCqcPracticeAutocompleteProps>;
  ClinicianTypeSelect: ComponentType<OnboardingClinicianTypeSelectProps>;
  organisationRoleMessages: Record<OrganisationRole, MessageDescriptor>;
  useAboutYouAnalytics: (
    params: OnboardingAboutYouAnalyticsParams
  ) => OnboardingAboutYouAnalytics;
  CountrySelect: ComponentType<OnboardingCountrySelectProps>;
  JurisdictionSelect: ComponentType<OnboardingJurisdictionSelectProps>;
  isEnterpriseRegion: (region: string | null | undefined) => boolean;
  enterpriseCountries: CountryType[];
  PhoneInput: JSXElementConstructor<OnboardingPhoneInputProps>;
  HeidiProFlashFilledSvg: ComponentType<OnboardingProFlashIconProps>;
  useIsFreeUser: () => { isFreeUser: boolean };
  useSelectedSpecialtyDisplayName: (
    selectedSpecialty: { id: string; name: string } | undefined,
    specialties: OnboardingSpecialty[] | undefined
  ) => string | undefined;
  otherSpecialtyId: string;
  useQuerySpecialties: (
    select: (data: OnboardingSpecialtiesResponse) => OnboardingSpecialty[],
    options?: { country?: string; enabled?: boolean }
  ) => { data: OnboardingSpecialty[] | undefined };
  useIsAuth0OnboardingGateEnabled: () => boolean;
  useOnboardingLocaleSync: (
    language: LanguageCode | null | undefined,
    options?: OnboardingLocaleSyncOptions
  ) => void;
  useWorkspaceNameDefault: () => (firstName: string) => string;
  ProductUsageDisclaimer: ComponentType;
  termsOfUseUrl: string;
  usagePolicyUrl: string;
  /** The app's country-to-region map, read through on every index: it can be overridden at runtime. */
  countriesToRegion: { readonly [countryName: string]: string | undefined };
  isCountryInRegion: (countryName: string, region: string) => boolean;
  isKnownRegion: (region: string) => boolean;
}

let loader: (() => OnboardingProfileHost) | null = null;
let resolved: OnboardingProfileHost | null = null;

export function configureOnboardingProfileHost(next: () => OnboardingProfileHost): void {
  loader = next;
  resolved = null;
}

export function getOnboardingProfileHost(): OnboardingProfileHost {
  if (resolved) return resolved;
  if (!loader) throw new Error('onboarding profile host is not configured');
  resolved = loader();
  return resolved;
}
